import React from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import SafeIcon from '../common/SafeIcon';
import { FiCalendar, FiLogOut, FiSettings, FiGlobe, FiShield } from 'react-icons/fi';
import { GeometricShapes } from '../components/GeometricShapes';

const Profile = () => {
  const { user, signOut } = useAuth();
  const { isDarkMode } = useApp();
  const { username, email, created_at, name } = user || {};

  const handleSignOut = () => {
    signOut();
    // Redirect handled by Layout component
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.3, delay: 0.2 } },
  };

  return (
    <div className={`min-h-screen p-4 sm:p-6 lg:p-8 relative overflow-hidden ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-800'}`}>
      <GeometricShapes />
      <motion.div
        className="max-w-4xl mx-auto z-10 relative"
        initial="hidden"
        animate="visible"
        variants={cardVariants}
      >
        <div className={`rounded-xl shadow-2xl p-6 sm:p-8 ${isDarkMode ? 'bg-gray-800/80 backdrop-blur-sm' : 'bg-white/80 backdrop-blur-sm'}`}>
          <div className="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left">
            <div className="relative mb-4 sm:mb-0 sm:mr-6">
              <div className={`w-24 h-24 sm:w-28 sm:h-28 rounded-full flex items-center justify-center text-5xl font-bold ${isDarkMode ? 'bg-primary-600 text-white' : 'bg-primary-500 text-white'}`}>
                {(username || name || 'U')?.charAt(0).toUpperCase()}
              </div>
              <div className={`absolute bottom-0 right-0 w-8 h-8 rounded-full flex items-center justify-center border-4 ${isDarkMode ? 'bg-green-400 border-gray-800' : 'bg-green-500 border-white'}`}>
                <SafeIcon icon={FiShield} className="w-4 h-4 text-white" />
              </div>
            </div>
            
            <div className="flex-grow">
              <h1 className="text-3xl sm:text-4xl font-bold text-primary-400">{username || name || 'Anonymous User'}</h1>
              <p className={`mt-1 text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{email}</p>
              {created_at && (
                <div className={`flex items-center justify-center sm:justify-start mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <SafeIcon icon={FiCalendar} className="mr-2" />
                  Member since {new Date(created_at).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>

          <hr className={`my-6 sm:my-8 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ProfileOption icon={FiSettings} text="Account Settings" />
            <ProfileOption icon={FiGlobe} text="Language & Region" />
          </div>

          <motion.button
            onClick={handleSignOut}
            className={`w-full mt-8 py-3 px-4 rounded-lg font-semibold flex items-center justify-center transition-all duration-300 ${isDarkMode ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-500 hover:bg-red-600 text-white'}`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            variants={itemVariants}
          >
            <SafeIcon icon={FiLogOut} className="mr-3 text-xl" />
            Sign Out
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

const ProfileOption = ({ icon, text }) => {
  const { isDarkMode } = useApp();
  return (
    <motion.div
      className={`p-4 rounded-lg flex items-center cursor-pointer transition-colors duration-200 ${isDarkMode ? 'bg-gray-700/50 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`}
      variants={{
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.3, delay: 0.4 } },
      }}
      whileHover={{ scale: 1.03 }}
    >
      <SafeIcon icon={icon} className="text-primary-400 text-2xl" />
      <span className="ml-4 font-medium">{text}</span>
    </motion.div>
  );
};

export default Profile;